import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { buildGoogleOAuthURL } from 'shared/frontend/googleOauthUrl';
import { environment } from 'src/environments/environment';
import { AdminSignInDto, ChangePassDto, ChangePassResponse, UserInfo } from '../models';
import { Logger, LogService } from '../util';
import { AbstractApiService } from './abstract-api-service.service';
import { DataResponse } from './shared-session.service';

@Injectable({
  providedIn: 'root',
})
export class SharedAccountService extends AbstractApiService {
  private readonly _logger: Logger;

  constructor(protected readonly httpClient: HttpClient, logService: LogService) {
    super(`${environment.backendServiceUrl}/v1/account`, httpClient);
    this._logger = logService.createLogger(SharedAccountService.name);
  }

  get googleOAuthURL(): string {
    return buildGoogleOAuthURL(environment.googleAuthUri, {
      client_id: environment.googleClientId,
      redirect_uri: environment.googleOauthRedirectUrl,
      response_type: 'code',
      scope: [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ],
      access_type: 'offline',
      prompt: 'consent',
      state: 'context=user', // Explicitly pass user context
    });
  }

  getGoogleOAuthURLWithReturnUrl(returnUrl: string): string {
    return buildGoogleOAuthURL(environment.googleAuthUri, {
      client_id: environment.googleClientId,
      redirect_uri: environment.googleOauthRedirectUrl,
      response_type: 'code',
      scope: [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ],
      access_type: 'offline',
      prompt: 'consent',
      state: `context=user&returnUrl=${encodeURIComponent(returnUrl)}`, // Explicitly pass user context with return URL
    });
  }

  getGoogleOAuthURLForOrganizer(returnUrl: string): string {
    return buildGoogleOAuthURL(environment.googleAuthUri, {
      client_id: environment.googleClientId,
      redirect_uri: environment.googleOauthRedirectUrl,
      response_type: 'code',
      scope: [
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ],
      access_type: 'offline',
      prompt: 'consent',
      state: `context=organizer&returnUrl=${encodeURIComponent(returnUrl)}`, // Include context in state
    });
  }

  async signinAdmin(credentials: AdminSignInDto): Promise<UserInfo | ChangePassResponse | null> {
    const res = await this.post<DataResponse<UserInfo | ChangePassResponse>>(
      '/admin/signin',
      credentials,
    );
    return res.body?.data || null;
  }

  async changeAdminPass(credentials: ChangePassDto): Promise<UserInfo | null> {
    const res = await this.post<DataResponse<UserInfo>>('/admin/password-change', credentials);
    return res.body?.data || null;
  }

  async logout(): Promise<{ message: string } | null> {
    this._logger.info('logout from AccountService');
    const res = await this.delete<DataResponse<{ message: string }>>('/signout');
    return res.body?.data || null;
  }
}
